import { Children, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER } from './internal/presets'

const TabPane = ({ children }) => children

/** Accepts the v5 `items` array or legacy `TabPane` children. */
const normalizeItems = (items, children) => {
  if (items?.length) return items
  return Children.toArray(children)
    .filter(Boolean)
    .map((child) => ({
      key: child.key?.replace(/^\.\$/, '') ?? child.props.tabKey,
      label: child.props.tab,
      children: child.props.children,
      disabled: child.props.disabled,
      closable: child.props.closable,
    }))
}

const POSITION_CLASSES = {
  top: 'flex-col',
  bottom: 'flex-col-reverse',
  left: 'flex-row',
  right: 'flex-row-reverse',
}

/**
 * antd Tabs. Note the project rule that tab-rendered pages get one route each:
 * this component only handles in-page tabs, routing stays in the page.
 */
const Tabs = ({
  items,
  children,
  activeKey,
  defaultActiveKey,
  onChange,
  onTabClick,
  onEdit,
  type = 'line',
  tabPosition = 'top',
  size = 'middle',
  centered = false,
  tabBarExtraContent,
  tabBarStyle,
  destroyInactiveTabPane = false,
  renderTabBar,
  className,
  style,
  ...rest
}) => {
  const { t } = useTranslation()
  const resolved = normalizeItems(items, children)
  const firstKey = resolved[0]?.key
  const [active, setActive] = useControllableValue(activeKey, defaultActiveKey ?? firstKey, onChange)
  const listRef = useRef(null)

  const isVertical = tabPosition === 'left' || tabPosition === 'right'

  const select = (item) => {
    if (item.disabled) return
    onTabClick?.(item.key)
    setActive(item.key)
  }

  // Roving arrow-key navigation, per the WAI-ARIA tabs pattern.
  const handleKeyDown = (event) => {
    const enabled = resolved.filter((item) => !item.disabled)
    const currentIndex = enabled.findIndex((item) => item.key === active)
    const nextKeys = isVertical ? ['ArrowDown', 'ArrowUp'] : ['ArrowRight', 'ArrowLeft']
    if (!nextKeys.includes(event.key)) return
    event.preventDefault()
    const delta = event.key === nextKeys[0] ? 1 : -1
    const next = enabled[(currentIndex + delta + enabled.length) % enabled.length]
    if (next) select(next)
  }

  const isCard = type === 'card' || type === 'editable-card'
  const customTabBar = typeof renderTabBar === 'function' ? renderTabBar() : null

  return (
    <div
      className={cn('ui-tabs flex', POSITION_CLASSES[tabPosition] || POSITION_CLASSES.top, className)}
      style={style}
      {...rest}
    >
      {renderTabBar != null ? customTabBar : (
      <div
        className={cn(
          'ui-tabs-nav flex shrink-0 items-center gap-2',
          isVertical ? `flex-col border-r ${BORDER}` : `border-b ${BORDER}`,
          centered && 'justify-center'
        )}
        style={tabBarStyle}
      >
        <div
          ref={listRef}
          role="tablist"
          aria-orientation={isVertical ? 'vertical' : 'horizontal'}
          onKeyDown={handleKeyDown}
          className={cn('ui-tabs-nav-list flex min-w-0 flex-1', isVertical ? 'flex-col' : 'overflow-x-auto')}
        >
          {resolved.map((item) => {
            const isActive = item.key === active
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`tab-${item.key}`}
                aria-selected={isActive}
                aria-controls={`tabpanel-${item.key}`}
                tabIndex={isActive ? 0 : -1}
                disabled={item.disabled}
                onClick={() => select(item)}
                className={cn(
                  'ui-tabs-tab inline-flex min-h-[var(--control-height)] shrink-0 items-center gap-2 whitespace-nowrap px-4 font-medium',
                  size === 'small' ? 'text-xs' : 'text-sm',
                  isCard
                    ? cn(
                        'border',
                        BORDER,
                        isActive
                          ? 'ui-tabs-tab-active border-b-transparent bg-[var(--surface-card)] text-[var(--primary-color)]'
                          : 'bg-[var(--gray-100)] text-[var(--text-muted)]'
                      )
                    : cn(
                        isVertical ? 'border-r-2' : 'border-b-2',
                        isActive
                          ? 'ui-tabs-tab-active border-[var(--primary-color)] text-[var(--primary-color)]'
                          : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-body)]'
                      ),
                  item.disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {item.icon}
                {item.label}
                {type === 'editable-card' && item.closable !== false ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Fermer ${item.label}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      onEdit?.(item.key, 'remove')
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.stopPropagation()
                      onEdit?.(item.key, 'remove')
                    }}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-[var(--gray-300)]"
                  >
                    <X size={10} aria-hidden="true" />
                  </span>
                ) : null}
              </button>
            )
          })}
          {type === 'editable-card' ? (
            <button
              type="button"
              aria-label={t('common.addTab')}
              onClick={() => onEdit?.(null, 'add')}
              className="ui-tabs-nav-add min-h-[var(--control-height)] px-3 text-sm"
            >
              +
            </button>
          ) : null}
        </div>
        {tabBarExtraContent ? (
          <div className="ui-tabs-extra-content shrink-0">
            {tabBarExtraContent.right ?? tabBarExtraContent.left ?? tabBarExtraContent}
          </div>
        ) : null}
      </div>
      )}

      <div className="ui-tabs-content-holder min-w-0 flex-1">
        {resolved.map((item) => {
          const isActive = item.key === active
          if (destroyInactiveTabPane && !isActive) return null
          return (
            <div
              key={item.key}
              role="tabpanel"
              id={`tabpanel-${item.key}`}
              aria-labelledby={`tab-${item.key}`}
              hidden={!isActive}
              className={cn('ui-tabs-tabpane pt-3', isActive && 'ui-tabs-tabpane-active')}
            >
              {item.children}
            </div>
          )
        })}
      </div>
    </div>
  )
}

Tabs.TabPane = TabPane

export default Tabs
